import { useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardTitle } from '@/shared/ui/card';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { MoreVertical, RefreshCcw, Router, Settings2, Trash2 } from 'lucide-react';
import Dropdown from '@/shared/components/Dropdown';
import { RouterData } from '@/shared/types/userTerminalTypes';
import ApplyRouterConfigDialog from '../dialogs/ApplyRouterConfigDialog';
import GenericAlertDialog from '@/shared/components/GenericAlertDialog';
import { useRebootRouter } from '../services/account-routers';
import { showErrorWithApiError, showSuccess } from '@/shared/utils/toast-utils';
import { ERROR, SUCCESS } from '@/config/toast-message-config';

interface RouterCardProps {
  router: RouterData;
  refetchTerminal: () => void;
}

const RouterCard = ({ router, refetchTerminal }: RouterCardProps) => {
  const [isApplyConfigDialogOpen, setIsApplyConfigDialogOpen] = useState(false);
  const [isRebootRouterDialogOpen, setIsRebootRouterDialogOpen] = useState(false);
  const rebootRouter = useRebootRouter();

  const dropdownItems = useMemo(() => {
    return [{
      label: 'Apply Wi-Fi config',
      icon: Settings2,
      variant: 'default' as const,
      onClick: () => {
        setIsApplyConfigDialogOpen(true);
      },
    }, {
      label: 'Reboot router',
      icon: RefreshCcw,
      variant: 'default' as const,
      onClick: () => {
        setIsRebootRouterDialogOpen(true);
      },
    }];
  }, []);

  const handleRebootRouter = async () => {
    try {
      await rebootRouter.mutateAsync({ routerId: router.routerId });
      showSuccess(SUCCESS.ROUTER.REBOOT_REQUESTED);
      setIsRebootRouterDialogOpen(false);
    } catch (error) {
      showErrorWithApiError(error, ERROR.ROUTER.REBOOT_REQUEST);
    }
  };

  return (
    <Card className="border border-border">
      <CardContent className="flex flex-col gap-4 p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-4 min-w-0">
            <div className="w-12 h-12 bg-background rounded-lg flex items-center justify-center border shrink-0">
              <Router className="h-6 w-6" />
            </div>
            <div className="min-w-0">
              <CardTitle className="text-lg truncate">{router.nickname || router.routerId}</CardTitle>
              <CardDescription className="text-sm truncate">
                {router.hardwareVersion || 'Starlink router'}
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {router.lastBonded != null && (
              <Badge variant={router.lastBonded ? 'default' : 'outline'}>
                {router.lastBonded ? 'Bonded' : 'Not bonded'}
              </Badge>
            )}
            {dropdownItems.length > 0 && (
              <Dropdown triggerIcon={MoreVertical} items={dropdownItems} />
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {router.configId ? (
            <Badge variant="outline">Config: {router.configId}</Badge>
          ) : (
            <span className="text-xs text-muted-foreground italic">No Wi-Fi config applied</span>
          )}
        </div>
      </CardContent>

      <ApplyRouterConfigDialog
        open={isApplyConfigDialogOpen}
        onOpenChange={setIsApplyConfigDialogOpen}
        router={router}
        refetchTerminal={refetchTerminal}
      />

      <GenericAlertDialog
        open={isRebootRouterDialogOpen}
        onOpenChange={setIsRebootRouterDialogOpen}
        title="Reboot Router"
        description="Are you sure you want to reboot this router?"
        confirmLabel="Reboot"
        cancelLabel="Cancel"
        onConfirm={handleRebootRouter}
        loading={rebootRouter.isPending}
      />
    </Card>
  );
};

export default RouterCard;
